import "../tracking-history/tracking-history.css";

import { useMsal } from "@azure/msal-react";
import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Input } from "@tql/ui-components-react/kendo/inputs";
import { Error } from "@tql/ui-components-react/kendo/labels";
import { Card, CardHeader } from "@tql/ui-components-react/kendo/layout";
import { Button } from "@tql/ui-components-react/tql/buttons";
import {
	check,
	send_01,
	xClose,
} from "@tql/ui-components-react/untitled/icons";
import { useEffect, useId, useState } from "react";
import { useParams } from "react-router";

import type { ShipmentRemark } from "~/shared/schemas/shipment-management-schema";

import { GroupedFeedList } from "../grouped-feed-list/grouped-feed-list.tsx";
import { RemarkItem } from "../remark-item/remark-item.tsx";
import { useDeleteRemark } from "./hooks/use-delete-remark.ts";
import { usePostNewRemark } from "./hooks/use-post-new-remark.ts";
import { usePutRemark } from "./hooks/use-put-remark.ts";

export const Remarks = ({
	details,
}: {
	details?: Record<string, ShipmentRemark[]>;
}) => {
	const { postNewRemark, isPostingNewRemark } = usePostNewRemark();
	const { putRemark, isPuttingRemark } = usePutRemark();
	const { deleteRemark, isDeletingRemark } = useDeleteRemark();
	const params = useParams();
	const { accounts } = useMsal();
	const currentUserName = accounts[0]?.name ?? null;
	const [message, setMessage] = useState("");
	const [selectedRemark, setSelectedRemark] = useState<ShipmentRemark | null>(
		null,
	);
	const [showError, setShowError] = useState(false);
	const errorId = useId();

	const isSaving = isPostingNewRemark || isPuttingRemark || isDeletingRemark;

	// Match the 15s auto-close used by toast error notifications elsewhere.
	useEffect(() => {
		if (!showError) return;
		const timer = setTimeout(() => setShowError(false), 15_000);
		return () => clearTimeout(timer);
	}, [showError]);

	// Shared reset for the remark form, used after a successful mutation and when
	// the user cancels an edit.
	const resetForm = () => {
		setMessage("");
		setSelectedRemark(null);
	};

	const handleSubmit = () => {
		if (!message.trim()) {
			setShowError(true);
			return;
		}
		setShowError(false);
		if (selectedRemark) {
			putRemark(
				{
					message,
					poNumber: params["poNumber"] ?? "",
					remark: selectedRemark,
				},
				{ onSuccess: resetForm },
			);
		} else {
			postNewRemark(
				{ message, poNumber: params["poNumber"] ?? "" },
				{ onSuccess: resetForm },
			);
		}
	};

	const handleRemarkSelect = (remark: ShipmentRemark, action: string) => {
		if (action === "Delete Message") {
			deleteRemark({
				remark,
				poNumber: params["poNumber"] ?? "",
			});
		} else {
			setSelectedRemark(remark);
			setMessage(remark.text);
		}
	};

	return (
		<Card>
			<CardHeader>
				<h3>Remarks</h3>
			</CardHeader>
			<div className={"tracking-input-container"}>
				<div className={"tracking-input-fields"}>
					<Input
						ariaDescribedBy={showError ? errorId : undefined}
						disabled={isSaving}
						onChange={(e) => {
							setMessage(e.target.value as string);
							if (showError) {
								setShowError(false);
							}
						}}
						placeholder={"Type a Message"}
						type={"text"}
						valid={!showError}
						value={message}
					/>
					{showError ? (
						<Error id={errorId}>Please enter a message.</Error>
					) : null}
				</div>
				{selectedRemark ? (
					<div className={"remark-edit-actions"}>
						<Button
							disabled={isSaving}
							onClick={resetForm}
							variant={"tertiary"}
						>
							<SvgIcon icon={xClose} size={"large"} />
						</Button>{" "}
						<Button
							disabled={isSaving}
							onClick={handleSubmit}
							variant={"tertiary"}
						>
							<SvgIcon icon={check} size={"large"} />
						</Button>
					</div>
				) : (
					<Button disabled={isSaving} onClick={handleSubmit}>
						<SvgIcon icon={send_01} size={"large"} />
					</Button>
				)}
			</div>
			<GroupedFeedList
				getItemKey={(remark) =>
					`${remark.dateEntered}-${remark.contactInfo.name}`
				}
				groups={details}
				isLoading={isSaving}
				renderItem={(remark) => (
					<RemarkItem
						canModify={
							currentUserName != null &&
							remark.contactInfo.name === currentUserName
						}
						isSaving={isSaving}
						onSelect={handleRemarkSelect}
						remark={remark}
					/>
				)}
			/>
		</Card>
	);
};
